import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, IconButton, SxProps, Theme, Tooltip } from "@mui/material";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useTranslation } from "react-i18next";
import { useUserStore } from "@/hooks/useUserStore";
import { ActionCodeEnum } from "@/domain/action.enum";
import { buildTimelineUrl } from "@/utils/troubleshooting.utils";

interface TimelineLinkButtonProps {
  participantId?: string;
  /** Anchor day (YYYY-MM-DD, account timezone). Defaults to today. */
  date?: string;
  /** Highlight the events linked to this geofence record. */
  geofenceId?: string;
  variant?: "icon" | "button";
  sx?: SxProps<Theme>;
}

/**
 * COYN-1507: entry point to the participant timeline. Renders nothing for
 * users whose roles lack the PART_TIMELINE_VIEW action (contract §9).
 */
const TimelineLinkButton: React.FC<TimelineLinkButtonProps> = ({
  participantId,
  date,
  geofenceId,
  variant = "button",
  sx,
}) => {
  const { t } = useTranslation("troubleshooting");
  const navigate = useNavigate();
  const { userHasAccessToCode } = useUserStore();

  if (
    !participantId ||
    !userHasAccessToCode(ActionCodeEnum.PART_TIMELINE_VIEW)
  ) {
    return null;
  }

  const handleClick = () =>
    navigate(buildTimelineUrl({ participantId, date, geofenceId }));

  if (variant === "icon") {
    return (
      <Tooltip title={t("openTimeline")}>
        <IconButton
          size="small"
          aria-label={t("openTimeline")}
          onClick={handleClick}
          sx={sx}
        >
          <TimelineIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      variant="outlined"
      startIcon={<TimelineIcon />}
      onClick={handleClick}
      sx={sx}
    >
      {t("openTimeline")}
    </Button>
  );
};

export default TimelineLinkButton;
